/**
 * Configuração de exibição da prévia da descrição (problemDescription) na
 * listagem principal de manutenções (MaintenanceList).
 *
 * O número máximo de caracteres da prévia é configurado por variável de
 * ambiente `VITE_DESCRIPTION_PREVIEW_MAX_CHARS`, com fallback padronizado
 * em 50 caracteres quando a env não está definida.
 *
 * O fallback também é usado como limite mínimo quando a env traz um valor
 * inválido (≤ 0 ou NaN) — desta forma nunca exibimos a prévia vazia por
 * misconfiguração.
 */
const DEFAULT_DESCRIPTION_PREVIEW_MAX_CHARS = 50

/**
 * Valor de `VITE_DESCRIPTION_PREVIEW_MAX_CHARS` injetado em build-time pelo
 * Vite (`define: { __DESCRIPTION_PREVIEW_MAX_CHARS__ }` em vite.config.ts);
 * vira string vazia quando a env não está definida no build.
 * Declarado globalmente em `src/vite-env.d.ts`.
 */
declare const __DESCRIPTION_PREVIEW_MAX_CHARS__: string

/**
 * Lê `VITE_DESCRIPTION_PREVIEW_MAX_CHARS` do token de build-time.
 * Retorna `undefined` quando a env não está definida no build (token vazio).
 *
 * Mesmo padrão de `version.ts` (`__APP_VERSION__` e afins): evita
 * `import.meta.env` direto, que quebra no Jest+CJS (SyntaxError de parse),
 * e substitui o `new Function('return import.meta')()` anterior — que
 * lançava SyntaxError no browser (import.meta só existe em módulos) e
 * fazia a env NUNCA ser lida em produção, sempre caindo no fallback 50.
 */
const readPreviewMaxCharsFromEnv = (): string | undefined => {
  const raw = __DESCRIPTION_PREVIEW_MAX_CHARS__
  return raw === '' ? undefined : raw
}

/**
 * Converte a string da env em número, garantindo que o valor final esteja
 * dentro de uma faixa razoável. Quando a env está ausente ou inválida,
 * devolve o fallback padronizado (50).
 */
const parsePreviewMaxChars = (raw: string | undefined): number => {
  if (raw === undefined || raw === null || raw === '') {
    return DEFAULT_DESCRIPTION_PREVIEW_MAX_CHARS
  }
  const parsed = Number(raw)
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return DEFAULT_DESCRIPTION_PREVIEW_MAX_CHARS
  }
  return Math.floor(parsed)
}

/**
 * Limite efetivo de caracteres para a prévia da descrição na listagem.
 * Lido da env `VITE_DESCRIPTION_PREVIEW_MAX_CHARS` no momento da
 * importação do módulo; o valor é congelado e re-utilizado em todas as
 * renderizações (não muda entre requests).
 */
export const DESCRIPTION_PREVIEW_MAX_CHARS: number = parsePreviewMaxChars(
  readPreviewMaxCharsFromEnv(),
)

/**
 * Versão textual da constante acima — útil em logs, debug e mensagens de
 * erro. Mantém a forma original da env (`string`) para diagnóstico.
 */
export const DESCRIPTION_PREVIEW_MAX_CHARS_SOURCE: string =
  readPreviewMaxCharsFromEnv() ?? String(DEFAULT_DESCRIPTION_PREVIEW_MAX_CHARS)

/**
 * Fallback padronizado exportado para que testes e utilitários possam
 * referenciar o mesmo número sem duplicar a constante.
 */
export { DEFAULT_DESCRIPTION_PREVIEW_MAX_CHARS }

/**
 * Trunca o texto da descrição preservando palavras sempre que possível.
 * Caso o texto já caiba dentro do limite, retorna-o inalterado.
 *
 * @param text texto original da descrição
 * @param max limite máximo de caracteres (default = env / 50)
 */
export const truncateDescriptionPreview = (
  text: string | undefined | null,
  max: number = DESCRIPTION_PREVIEW_MAX_CHARS,
): string => {
  if (!text) return ''
  const trimmed = text.trim()
  if (trimmed.length <= max) return trimmed

  const slice = trimmed.slice(0, max)
  const lastSpace = slice.lastIndexOf(' ')
  if (lastSpace > max * 0.6) {
    return `${slice.slice(0, lastSpace).trimEnd()}…`
  }
  return `${slice.trimEnd()}…`
}